// Import via the local `./hl7` wrapper rather than `'hl7v2'` directly — the wrapper installs
// the browser `Buffer` polyfill before the library loads. See `./hl7.ts` for details.
import type {
  HL7Field,
  HL7Repetition,
} from '@ctw/shared/content/document-viewer/_internal/renderers/hl7/hl7';
import {
  ADDRESS_TYPE_LABELS,
  lookupHl7Code,
  PERSON_LOCATION_TYPE_LABELS,
  TELECOM_EQUIPMENT_LABELS,
  TELECOM_USE_LABELS,
} from '@ctw/shared/content/document-viewer/_internal/renderers/hl7/hl7-code-tables';
import {
  formatHl7Timestamp,
  formatHl7TimestampIsoForDateTimeAttribute,
  parseHl7Timestamp,
} from '@ctw/shared/content/document-viewer/_internal/renderers/hl7/hl7-timestamp';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { ReactNode } from 'react';

/**
 * Datatype-aware field renderer for HL7 v2 messages (ADT, ORU, MDM, etc.). Dispatches each
 * `HL7Field` to a per-datatype renderer that interprets the field's component structure
 * semantically — XPN becomes "Bruno M Zhang Mr.", XAD becomes a multi-line address block, TS
 * becomes "Jan 31, 1972", CWE becomes the display text plus a tooltip with the raw code, and
 * so on.
 *
 * For datatypes the dispatcher doesn't recognize, falls back to a raw string with subtle `^`
 * separators so the user still sees something useful.
 *
 * **Spec reference.** All datatype names below match HL7 v2.5 §2.A. The canonical online
 * reference is the Caristix definition browser:
 * https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes — individual datatype pages are
 * linked from each per-datatype renderer's docblock.
 */

// ---------------------------------------------------------------------------
// Component-position constants per HL7 v2.5 datatype.
//
// These mirror the bidirectional maps exposed by `hl7v2-dictionary` (`XPNType.FamilyName`
// === 1, etc.) but we redeclare them here as numeric constants so the call sites read
// naturally (`comp(XPN_FAMILY)` instead of `comp(1)`) without leaning on the dictionary
// package's loose typing.
// ---------------------------------------------------------------------------

// XPN — Extended Person Name. https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XPN
const XPN_FAMILY = 1;
const XPN_GIVEN = 2;
const XPN_MIDDLE = 3;
const XPN_SUFFIX = 4;
const XPN_PREFIX = 5;
const XPN_DEGREE = 6;

// XAD — Extended Address. https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XAD
const XAD_STREET = 1;
const XAD_OTHER_DESIGNATION = 2;
const XAD_CITY = 3;
const XAD_STATE = 4;
const XAD_ZIP = 5;
const XAD_COUNTRY = 6;
const XAD_TYPE = 7;

// CWE / CE / CNE — Coded value. The first three components carry the same meaning across all
// three flavors so we share constants. https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/CWE
const CODED_IDENTIFIER = 1;
const CODED_TEXT = 2;
const CODED_CODING_SYSTEM = 3;

// XCN — Extended Composite ID + Name (typically a provider).
// https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XCN
const XCN_ID = 1;
const XCN_FAMILY = 2;
const XCN_GIVEN = 3;
const XCN_MIDDLE = 4;
const XCN_SUFFIX = 5;
const XCN_PREFIX = 6;
const XCN_DEGREE = 7;
// XCN-13 is the assigning-authority/identifier-type pair we surface as the ID context.
const XCN_ASSIGNING_AUTHORITY_TYPE = 13;

// XTN — Extended Telecommunication Number.
// https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XTN
const XTN_NUMBER_FORMATTED = 1;
const XTN_USE_CODE = 2;
const XTN_EQUIPMENT_TYPE = 3;
const XTN_COMMUNICATION_ADDRESS = 4;
const XTN_AREA_CITY_CODE = 6;
const XTN_LOCAL_NUMBER = 7;
const XTN_EXTENSION = 8;

// CX — Extended Composite ID with Check Digit.
// https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/CX
const CX_ID_NUMBER = 1;
const CX_ASSIGNING_AUTHORITY = 4;
const CX_ID_TYPE_CODE = 5;

// EI — Entity Identifier. https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/EI
const EI_ENTITY_ID = 1;
const EI_NAMESPACE = 2;

// HD — Hierarchic Designator. https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/HD
const HD_NAMESPACE = 1;
const HD_UNIVERSAL_ID = 2;
const HD_UNIVERSAL_ID_TYPE = 3;

// PL — Person Location. https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/PL
const PL_POINT_OF_CARE = 1;
const PL_ROOM = 2;
const PL_BED = 3;
const PL_FACILITY = 4;
const PL_PERSON_LOCATION_TYPE = 6;
const PL_BUILDING = 7;
const PL_FLOOR = 8;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read a single component as a raw, **trimmed** HL7 string. Uses `toHL7String()` rather than
 * `getValue()` because the latter returns `any` — for primitive components it's a string, but
 * for the occasional composite component (e.g. XCN-13 is a HD composite) it can return an
 * object, which `String(obj)` would stringify to "[object Object]". `toHL7String()` always
 * returns a string with the expected wire format.
 *
 * Trimming here matters: HL7 fields sometimes carry stray whitespace, and `field.isEmpty()`
 * upstream treats whitespace-only as non-empty, so without trimming we'd render visually-blank
 * cells without an em-dash placeholder.
 */
const getComponentString = (rep: HL7Repetition, position: number): string => {
  try {
    return rep.component(position).toHL7String()?.trim() ?? '';
  } catch {
    return '';
  }
};

/**
 * Read a whole repetition as a raw, trimmed HL7 string. Same trim rationale as
 * `getComponentString`. Use this anywhere the renderer would have called `rep.toHL7String()`
 * directly so the whitespace-empty case routes to `<EmptyDash />`.
 */
const getRepString = (rep: HL7Repetition): string => {
  try {
    return rep.toHL7String()?.trim() ?? '';
  } catch {
    return '';
  }
};

/** Em-dash placeholder for empty values. */
const EmptyDash = () => <span className={tw`text-content-subtle`}>—</span>;

/**
 * Visible separator between annotation items and inside hierarchical paths. The plain `·`
 * glyph is too easily missed against `text-xs` content, so this component renders it at
 * `text-base` with a small horizontal margin so it reads as a real separator rather than a
 * stray dot.
 *
 * `aria-hidden` because the dot is purely decorative — assistive tech reads the surrounding
 * spaces between items as the natural separator.
 */
const MiddleDot = () => (
  <span className={tw`mx-1 text-base leading-none`} aria-hidden="true">
    ·
  </span>
);

/**
 * Join a list of strings with `<MiddleDot />` separators. Empty / falsy entries are dropped.
 * Returns a Fragment of nodes so callers can place it inside any inline parent.
 */
const joinWithMiddleDot = (parts: Array<string | undefined | null>): ReactNode => {
  const filtered = parts.filter((p): p is string => Boolean(p && p.length > 0));
  if (filtered.length === 0) {
    return null;
  }
  return (
    <>
      {filtered.map((part, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional, no reordering possible
        <span key={index}>
          {index > 0 && <MiddleDot />}
          {part}
        </span>
      ))}
    </>
  );
};

/**
 * Render a code as a small, monospaced badge.
 *
 * Two visual weights:
 *
 *   - **default** (high contrast) — used when the code IS the value (no human-readable display
 *     accompanies it). E.g. PV1-1 Set ID, MSH-12 Version, MSH-19 Country Code. Reads as
 *     normal-weight body text in a tinted pill.
 *   - **`deemphasized`** — used when the code sits NEXT TO a human-readable display (e.g.
 *     PID-10 race `2106-3` paired with "White"). The pill drops to subtle text color so the
 *     prose is the dominant element on the row and the code reads as supplementary metadata.
 */
const CodeBadge = ({ code, deemphasized }: { code: string; deemphasized?: boolean }) => (
  <span
    className={twx(
      'inline-block rounded bg-background-subtle px-1.5 py-0.5 align-bottom font-mono text-xs leading-none',
      deemphasized && 'text-content-subtle',
    )}
  >
    {code}
  </span>
);

/**
 * Resolve a coded fallback display by walking up to the field/segment context. Looks up the
 * code against the segment+position-keyed mini-table in `hl7-code-tables.ts`. Returns
 * undefined when no fallback applies.
 */
const lookupCodedFallback = (rep: HL7Repetition, code: string): string | undefined => {
  if (!code) {
    return undefined;
  }
  return lookupHl7Code(rep.segment.segmentType, rep.field.position, code);
};

// Hoisted regexes for `joinComponentsWithSpace` (Biome `useTopLevelRegex`).
const HL7_COMPONENT_SEPARATOR_RUN_PATTERN = /\^+/g;
const COLLAPSIBLE_WHITESPACE_PATTERN = /\s+/g;

/**
 * Replace HL7 component separators (`^`) in a raw fallback value with spaces, so a leaked
 * composite like `Zhang^Bruno^M` reads as `Zhang Bruno M` and a sub-composite like
 * `BIGBOX^^OID` reads as `BIGBOX OID`. We *don't* render `^` glyphs anymore — when the
 * dispatcher hits a structured renderer (XPN/XAD/CWE/…) the components are decoded and
 * formatted natively; when it falls through to this helper, the user benefits more from
 * clean prose than from visible parser plumbing.
 *
 * Runs of consecutive separators (`^^^`) collapse to a single space so empty intermediate
 * components don't widen the gap visually.
 */
const joinComponentsWithSpace = (text: string): string =>
  text
    .replace(HL7_COMPONENT_SEPARATOR_RUN_PATTERN, ' ')
    .replace(COLLAPSIBLE_WHITESPACE_PATTERN, ' ')
    .trim();

// Hoisted regexes for the `looksLikeCode` heuristic (Biome `useTopLevelRegex`).
const ANY_WHITESPACE_PATTERN = /\s/;
const HL7_SEPARATOR_CHAR_PATTERN = /[\^~]/;

/**
 * Heuristic: does this raw string read more like a "code" than like content? Used inside
 * `RawFallback` to decide whether to render with `CodeBadge` styling. We badge ST/PT/VID/etc
 * fields that the dispatcher doesn't have a structured renderer for but which a human reader
 * unambiguously perceives as a code (`P`, `2.5`, `USA`, `^~\&`, `AL`, `NE`).
 *
 * Rules:
 *   - Reject anything containing whitespace (it's prose, not a code).
 *   - Reject anything longer than 16 chars (codes are short).
 *   - When `^` or `~` separators are present, only treat as a code if the whole value is very
 *     short (≤6 chars). This badges the encoding-chars field (`^~\&`) without accidentally
 *     badging separator-stuffed content like a person name leaked into an IS-typed field.
 */
const looksLikeCode = (text: string): boolean => {
  if (ANY_WHITESPACE_PATTERN.test(text)) {
    return false;
  }
  if (text.length === 0 || text.length > 16) {
    return false;
  }
  if (HL7_SEPARATOR_CHAR_PATTERN.test(text)) {
    return text.length <= 6;
  }
  return true;
};

/**
 * Generic fallback: HL7 raw string wrapped in `break-words`. Any embedded `^` separators are
 * collapsed to spaces via `joinComponentsWithSpace` so a leaked composite reads cleanly as
 * prose (`Zhang^Bruno^M` → `Zhang Bruno M`). Used for unknown datatypes and when a structured
 * renderer throws. When the value looks like a short opaque code (see `looksLikeCode`), it's
 * promoted to `CodeBadge` styling so it reads consistently with other coded fields.
 */
const RawFallback = ({ rawValue }: { rawValue: string }) => {
  // Treat whitespace-only as empty — `field.isEmpty()` upstream considers whitespace as
  // content, so without this defensive check a `' '` cell would render as a literal space
  // (visually empty, but no em-dash).
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return <EmptyDash />;
  }
  if (looksLikeCode(trimmed)) {
    return <CodeBadge code={trimmed} />;
  }
  return <span className={tw`break-words`}>{joinComponentsWithSpace(rawValue)}</span>;
};

// ---------------------------------------------------------------------------
// Per-datatype renderers — each takes a single HL7Repetition.
// ---------------------------------------------------------------------------

type RepetitionRenderer = (rep: HL7Repetition) => ReactNode;

/**
 * XPN — Extended Person Name. Format: "Prefix Given Middle Family Suffix, Degree".
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XPN
 */
const renderXpn: RepetitionRenderer = (rep) => {
  const family = getComponentString(rep, XPN_FAMILY);
  const given = getComponentString(rep, XPN_GIVEN);
  const middle = getComponentString(rep, XPN_MIDDLE);
  const suffix = getComponentString(rep, XPN_SUFFIX);
  const prefix = getComponentString(rep, XPN_PREFIX);
  const degree = getComponentString(rep, XPN_DEGREE);

  const nameParts = [prefix, given, middle, family, suffix].filter(Boolean);
  const formatted = nameParts.join(' ');

  if (!(formatted || degree)) {
    return <EmptyDash />;
  }

  return (
    <span>
      <span>{formatted}</span>
      {degree && <span className={tw`text-content-subtle`}>, {degree}</span>}
    </span>
  );
};

/**
 * XAD — Extended Address. Multi-line block. The XAD-7 type code (Home, Work, Mailing, …) is
 * resolved through `ADDRESS_TYPE_LABELS` (HL7 table 0190).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XAD
 */
const renderXad: RepetitionRenderer = (rep) => {
  const street = getComponentString(rep, XAD_STREET);
  const other = getComponentString(rep, XAD_OTHER_DESIGNATION);
  const city = getComponentString(rep, XAD_CITY);
  const state = getComponentString(rep, XAD_STATE);
  const zip = getComponentString(rep, XAD_ZIP);
  const country = getComponentString(rep, XAD_COUNTRY);
  const type = getComponentString(rep, XAD_TYPE);

  if (!(street || city || state || zip)) {
    return <EmptyDash />;
  }

  const line1 = [street, other].filter(Boolean).join(', ');
  const cityState = [city, state].filter(Boolean).join(', ');
  const line2Parts = [cityState, zip].filter(Boolean);
  if (country) {
    line2Parts.push(country);
  }
  const line2 = line2Parts.join(' ');
  const typeLabel = ADDRESS_TYPE_LABELS[type] ?? (type ? `(${type})` : '');

  // Address-type annotation (Home / Work / Mailing) sits BELOW the address block — same
  // stacked treatment we use for phone-number metadata so identifying labels don't compete
  // visually with the data they describe.
  return (
    <address className={tw`flex flex-col not-italic leading-tight`}>
      {line1 && <span>{line1}</span>}
      {line2 && <span>{line2}</span>}
      {typeLabel && <span className={tw`text-content-subtle text-xs`}>{typeLabel}</span>}
    </address>
  );
};

/**
 * TS / DTM — Timestamp. Format-aware via `parseHl7Timestamp`. Both datatypes share the same
 * `YYYY[MM[DD[HH[MM[SS[.S+]]]]]][+/-ZZZZ]` wire format (see `./hl7-timestamp.ts`).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/TS
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/DTM
 */
const renderTimestamp: RepetitionRenderer = (rep) => {
  const raw = getRepString(rep);
  if (!raw) {
    return <EmptyDash />;
  }
  const parsed = parseHl7Timestamp(raw);
  if (!parsed) {
    return <RawFallback rawValue={raw} />;
  }
  return (
    <time
      dateTime={formatHl7TimestampIsoForDateTimeAttribute(parsed)}
      className={tw`tabular-nums`}
      title={raw}
    >
      {formatHl7Timestamp(parsed)}
    </time>
  );
};

/**
 * DT — Date only. Same parser as TS/DTM; precision ends up at "day" or coarser.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/DT
 */
const renderDate: RepetitionRenderer = (rep) => renderTimestamp(rep);

/**
 * CWE / CE / CNE — Coded value. Prefer inline display text; fall back to code-table lookup;
 * final fallback is the bare code. The code is rendered as a small mono badge next to the
 * display text so it remains visually distinct from neighbouring deprecated text-only fields
 * (DG1-3 vs DG1-4) — when the same description appears twice, only the coded one carries the
 * badge so the duplication is clearly de-emphasized.
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/CWE — Coded with Exceptions (v2.5)
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/CE — Coded Element (predecessor)
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/CNE — Coded with No Exceptions
 */
const renderCodedValue: RepetitionRenderer = (rep) => {
  const code = getComponentString(rep, CODED_IDENTIFIER);
  const inlineText = getComponentString(rep, CODED_TEXT);
  const codingSystem = getComponentString(rep, CODED_CODING_SYSTEM);

  if (!(code || inlineText)) {
    return <EmptyDash />;
  }

  const resolvedDisplay = inlineText || lookupCodedFallback(rep, code);
  // HTML `title` attribute renders newlines as spaces in most browsers, so use a visible bullet
  // rather than `\n` for the multi-part tooltip.
  const tooltip = [code && `Code: ${code}`, codingSystem && `System: ${codingSystem}`]
    .filter(Boolean)
    .join(' · ');

  // Code only, no resolved display (no inline text, no lookup hit) — examples: MSH-19 `en`,
  // PD1-11 `N`, PD1-15 `A`. The bare code IS the value; render as a `CodeBadge` so it reads
  // consistently with every other code in the table rather than as plain text.
  if (code && !resolvedDisplay) {
    return <CodeBadge code={code} />;
  }

  // Stacked layout when both code and display are available: human-readable display sits ON
  // TOP, deemphasized code badge underneath. The display is what a clinical reader actually
  // wants to absorb (`White`, `Chest pain, unspecified`); the code becomes supplementary
  // metadata. Left-aligned so long descriptions can wrap without the badge being pushed
  // off-line, and `gap-0.5` keeps the two lines visually grouped.
  return (
    <span className={tw`inline-flex flex-col items-start gap-0.5`} title={tooltip || undefined}>
      {resolvedDisplay && <span>{resolvedDisplay}</span>}
      {code && <CodeBadge code={code} deemphasized={true} />}
    </span>
  );
};

/**
 * XCN — Extended Composite ID + Name. Provider names with optional NPI/identifier context.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XCN
 */
const renderXcn: RepetitionRenderer = (rep) => {
  const id = getComponentString(rep, XCN_ID);
  const family = getComponentString(rep, XCN_FAMILY);
  const given = getComponentString(rep, XCN_GIVEN);
  const middle = getComponentString(rep, XCN_MIDDLE);
  const suffix = getComponentString(rep, XCN_SUFFIX);
  const prefix = getComponentString(rep, XCN_PREFIX);
  const degree = getComponentString(rep, XCN_DEGREE);
  const idType = getComponentString(rep, XCN_ASSIGNING_AUTHORITY_TYPE);

  const nameParts = [prefix, given, middle, family, suffix].filter(Boolean);
  const name = nameParts.join(' ');

  if (!(name || id)) {
    return <EmptyDash />;
  }

  // Layout:
  //   Name, Credential          ← both at the same emphasis (a clinician's "MD" is part of
  //   ID-TYPE 1234567890           how they identify themselves, not metadata about them)
  //   ID-TYPE 1234567890        ← stacked below at smaller subtle styling — the identifier
  //                                lines up vertically with the name above it rather than
  //                                trailing off to the right in parentheses
  return (
    <span className={tw`flex flex-col leading-tight`}>
      <span>
        {name || id}
        {name && degree && <span>, {degree}</span>}
      </span>
      {id && name && (
        <span className={tw`text-content-subtle text-xs`}>
          {/* XCN-13 is itself an HD composite; collapse any inner `^` to spaces via
           * `joinComponentsWithSpace` so a `BIGBOX^^OID`-shaped slot reads as `BIGBOX OID`. */}
          {idType ? `${joinComponentsWithSpace(idType)} ${id}` : id}
        </span>
      )}
    </span>
  );
};

// Inserts a space after the closing area-code paren when XTN-1 arrives in the common
// no-space form `(646)555-1188`. Hoisted per the `useTopLevelRegex` Biome rule.
const XTN_AREA_CODE_PAREN_PATTERN = /^(\(\d+\))(\S)/;

/**
 * Apply a light normalization pass to a pre-formatted XTN-1 phone number — currently just
 * inserts a space between `(area)` and the local number when the source omits it (`(646)555`
 * → `(646) 555`). Anything that doesn't match the pattern is returned unchanged.
 */
const normalizePhoneFormatting = (formatted: string): string =>
  formatted.replace(XTN_AREA_CODE_PAREN_PATTERN, '$1 $2');

/**
 * XTN — Extended Telecommunication Number. Picks a primary value via the spec's component
 * priority (pre-formatted XTN-1 → composed XTN-6/XTN-7 → raw XTN-4 communication address) and
 * wraps phone-like values in `tel:` and email-like values in `mailto:` so click-to-call and
 * click-to-email work in clinical desktop apps.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/XTN
 */
const renderXtn: RepetitionRenderer = (rep) => {
  const formatted = getComponentString(rep, XTN_NUMBER_FORMATTED);
  const useCode = getComponentString(rep, XTN_USE_CODE);
  const equipmentType = getComponentString(rep, XTN_EQUIPMENT_TYPE);
  const communicationAddress = getComponentString(rep, XTN_COMMUNICATION_ADDRESS);
  const areaCity = getComponentString(rep, XTN_AREA_CITY_CODE);
  const local = getComponentString(rep, XTN_LOCAL_NUMBER);
  const extension = getComponentString(rep, XTN_EXTENSION);

  let primary = formatted ? normalizePhoneFormatting(formatted) : formatted;
  if (!primary && (areaCity || local)) {
    primary = [areaCity && `(${areaCity})`, local].filter(Boolean).join(' ');
  }
  if (!primary && communicationAddress) {
    primary = communicationAddress;
  }
  if (!primary) {
    return <EmptyDash />;
  }

  const useLabel = TELECOM_USE_LABELS[useCode];
  const equipmentLabel = TELECOM_EQUIPMENT_LABELS[equipmentType];
  const annotationsNode = joinWithMiddleDot([useLabel, equipmentLabel]);

  const primaryEl = <span className={tw`tabular-nums`}>{primary}</span>;

  // Telecom-use / equipment annotations stack BELOW the number, mirroring the address-type
  // treatment: identifying labels shouldn't compete inline with the data they describe. The
  // extension stays inline because it's part of the dialing string itself.
  return (
    <span className={tw`flex flex-col leading-tight`}>
      <span>
        {primaryEl}
        {extension && <span className={tw`text-content-subtle text-xs`}> ext. {extension}</span>}
      </span>
      {annotationsNode && (
        <span className={tw`text-content-subtle text-xs`}>{annotationsNode}</span>
      )}
    </span>
  );
};

/**
 * CX — Extended Composite ID with Check Digit. Identifier with assigning authority context.
 * CX-4 is itself an HD composite; the renderer collapses its `^` separators to spaces.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/CX
 */
const renderCx: RepetitionRenderer = (rep) => {
  const idNumber = getComponentString(rep, CX_ID_NUMBER);
  const assigningAuthority = getComponentString(rep, CX_ASSIGNING_AUTHORITY);
  const idTypeCode = getComponentString(rep, CX_ID_TYPE_CODE);

  if (!idNumber) {
    return <EmptyDash />;
  }

  // CX-4 is itself an HD composite (`namespace^universalId^type`); collapse it via
  // `joinComponentsWithSpace` so the assigning authority reads as `BIGBOX OID` instead of
  // `BIGBOX^^OID`. Annotations stack BELOW the identifier so the ID itself stays the dominant
  // visual element on the row, and the ID number itself wears full `CodeBadge` styling for
  // visual consistency with other coded fields.
  return (
    <span className={tw`flex flex-col items-start leading-tight`}>
      <CodeBadge code={idNumber} />
      {(idTypeCode || assigningAuthority) && (
        <span className={tw`text-content-subtle text-xs`}>
          {idTypeCode}
          {idTypeCode && assigningAuthority && ' '}
          {assigningAuthority && <>@ {joinComponentsWithSpace(assigningAuthority)}</>}
        </span>
      )}
    </span>
  );
};

/**
 * EI — Entity Identifier.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/EI
 */
const renderEi: RepetitionRenderer = (rep) => {
  const entityId = getComponentString(rep, EI_ENTITY_ID);
  const namespace = getComponentString(rep, EI_NAMESPACE);

  if (!entityId) {
    return <EmptyDash />;
  }

  return (
    <span className={tw`flex flex-col items-start leading-tight`}>
      <CodeBadge code={entityId} />
      {namespace && <span className={tw`text-content-subtle text-xs`}>@ {namespace}</span>}
    </span>
  );
};

/**
 * HD — Hierarchic Designator. Renders the namespace as the primary label and surfaces the
 * universal ID and/or its type as a subtle annotation. e.g. `BIGBOX_HEALTH^^NPI` becomes
 * `BIGBOX_HEALTH (NPI)`; `BIGBOX_HEALTH^123^OID` becomes `BIGBOX_HEALTH (123 / OID)`.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/HD
 */
const renderHd: RepetitionRenderer = (rep) => {
  const namespace = getComponentString(rep, HD_NAMESPACE);
  const universalId = getComponentString(rep, HD_UNIVERSAL_ID);
  const universalIdType = getComponentString(rep, HD_UNIVERSAL_ID_TYPE);

  if (!(namespace || universalId || universalIdType)) {
    return <EmptyDash />;
  }

  const annotation = [universalId, universalIdType].filter(Boolean).join(' / ');

  return (
    <span className={tw`flex flex-col leading-tight`}>
      <span>{namespace || universalId || universalIdType}</span>
      {namespace && annotation && (
        <span className={tw`text-content-subtle text-xs`}>{annotation}</span>
      )}
    </span>
  );
};

/**
 * PL — Person Location. Renders a hierarchical "Facility · Building Floor · Point of Care ·
 * Room · Bed" path. The PL-6 location-type code (Clinic, Department, Home, …) is resolved
 * through `PERSON_LOCATION_TYPE_LABELS` (HL7 table 0305).
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/PL
 */
const renderPl: RepetitionRenderer = (rep) => {
  const pointOfCare = getComponentString(rep, PL_POINT_OF_CARE);
  const room = getComponentString(rep, PL_ROOM);
  const bed = getComponentString(rep, PL_BED);
  const facility = getComponentString(rep, PL_FACILITY);
  const personLocationType = getComponentString(rep, PL_PERSON_LOCATION_TYPE);
  const building = getComponentString(rep, PL_BUILDING);
  const floor = getComponentString(rep, PL_FLOOR);

  // Build a coarse-to-fine description: facility/building/floor → unit/point-of-care → room → bed.
  const parts: Array<string> = [];
  if (facility) {
    parts.push(facility);
  }
  const buildingFloor = [building, floor].filter(Boolean).join(' ');
  if (buildingFloor) {
    parts.push(buildingFloor);
  }
  if (pointOfCare) {
    parts.push(pointOfCare);
  }
  if (room) {
    parts.push(`Room ${room}`);
  }
  if (bed) {
    parts.push(`Bed ${bed}`);
  }

  if (parts.length === 0) {
    return <EmptyDash />;
  }

  const typeLabel = personLocationType
    ? PERSON_LOCATION_TYPE_LABELS[personLocationType]
    : undefined;

  // PV1-11 (Temporary Location) is typed PL by `hl7v2` but real messages routinely stuff a
  // bare service code into it (`CARD`, `ED`, `ONC`, `IM`, …). When only one component is
  // populated and it reads as a short opaque code, surface it as a `CodeBadge` so it sits
  // in the same visual register as every other coded field.
  if (parts.length === 1 && looksLikeCode(parts[0])) {
    return (
      <span className={tw`flex flex-col items-start leading-tight`}>
        <CodeBadge code={parts[0]} />
        {typeLabel && <span className={tw`text-content-subtle text-xs`}>{typeLabel}</span>}
      </span>
    );
  }

  return (
    <span className={tw`flex flex-col leading-tight`}>
      <span>{joinWithMiddleDot(parts)}</span>
      {typeLabel && <span className={tw`text-content-subtle text-xs`}>{typeLabel}</span>}
    </span>
  );
};

/**
 * IS / ID — Primitive coded value (single string code, no internal components). Used for fields
 * like PID-8 (sex `M`/`F`), PV1-2 (patient class `I`/`O`), PV1-4 (admission type), DG1-6
 * (diagnosis type) — places where HL7 v2.5 dropped to a primitive instead of CWE because the
 * field's table is fixed and short. Lookup goes straight to the field-keyed fallback table.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/IS
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/ID
 */
const renderPrimitiveCoded: RepetitionRenderer = (rep) => {
  const code = getRepString(rep);
  if (!code) {
    return <EmptyDash />;
  }
  // Defensive: a primitive-coded field should never legitimately carry `^` separators, but the
  // `hl7v2` dictionary mistypes a few segments (e.g. IN1-15 — see
  // https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/IN1.15 — is declared `IS` but real
  // messages stuff an XPN-shaped value there). Route any such value through `RawFallback` so
  // the leaked separators are deemphasized rather than rendered as plain text alongside real
  // content.
  if (code.includes('^')) {
    return <RawFallback rawValue={code} />;
  }
  const display = lookupCodedFallback(rep, code);
  if (!display) {
    // No spec-table label available — render the bare code as a `CodeBadge` so it reads
    // consistently with every other code in the table (PV1-11 VIP indicator, AL1-2 allergy
    // type, …) rather than as plain monospace text.
    return <CodeBadge code={code} />;
  }
  // Display-above-code stacked — same treatment as `renderCodedValue` so all coded fields
  // read consistently across the table regardless of whether they came in as CWE or IS/ID.
  // The human-readable display (`Female`, `Inpatient`, …) is the primary value; the bare
  // code drops to deemphasized styling underneath.
  return (
    <span className={tw`inline-flex flex-col items-start gap-0.5`}>
      <span>{display}</span>
      <CodeBadge code={code} deemphasized={true} />
    </span>
  );
};

/**
 * SI — Set ID. A 4-digit-or-fewer counter the spec uses on every repeating segment's first
 * field (PV1-1, OBX-1, NK1-1, etc.) to disambiguate instances. Render as a `CodeBadge` so it
 * reads consistently with every other code in the table.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/SI
 */
const renderSetId: RepetitionRenderer = (rep) => {
  const id = getRepString(rep);
  if (!id) {
    return <EmptyDash />;
  }
  return <CodeBadge code={id} />;
};

/**
 * NM — Numeric. Format with thousands separators when the value is a real number.
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/NM
 */
const renderNm: RepetitionRenderer = (rep) => {
  const raw = getRepString(rep);
  if (!raw) {
    return <EmptyDash />;
  }
  const numeric = Number.parseFloat(raw);
  if (Number.isFinite(numeric)) {
    return <span className={tw`tabular-nums`}>{numeric.toLocaleString()}</span>;
  }
  // Non-numeric content in an NM field is malformed — surface via `RawFallback` so any embedded
  // separators get the same deemphasis treatment as the rest of the table.
  return <RawFallback rawValue={raw} />;
};

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

const RENDERERS: Record<string, RepetitionRenderer> = {
  XPN: renderXpn,
  XAD: renderXad,
  TS: renderTimestamp,
  DTM: renderTimestamp,
  DT: renderDate,
  CWE: renderCodedValue,
  CE: renderCodedValue,
  CNE: renderCodedValue,
  XCN: renderXcn,
  XTN: renderXtn,
  CX: renderCx,
  EI: renderEi,
  HD: renderHd,
  PL: renderPl,
  NM: renderNm,
  SI: renderSetId,
  IS: renderPrimitiveCoded,
  ID: renderPrimitiveCoded,
};

/**
 * Render an HL7 field according to its declared datatype. Wraps each repetition (HL7's `~`
 * separator, MSH-2 encoding-character #2) on its own row. If the datatype is unknown or any
 * renderer throws, falls back to the raw HL7 string with subtle `^` separators so the user
 * still sees the data.
 *
 * Empty repetitions are filtered out before rendering. Sparse repetition fields (e.g.
 * `value~~other`) and edge cases like MSH-2 — where the `~` separator characters appear inside
 * the encoding-chars value and the parser can split them into multiple repetitions — would
 * otherwise stack an em-dash next to the real value.
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.2 — Encoding Characters
 */
export const renderHl7Field = (field: HL7Field): ReactNode => {
  const datatype = field.definition?.type ?? '';
  const renderer = RENDERERS[datatype];
  const populatedReps = field.repetitions.filter((rep) => getRepString(rep) !== '');

  const renderOne = (rep: HL7Repetition): ReactNode => {
    if (!renderer) {
      return <RawFallback rawValue={getRepString(rep)} />;
    }
    try {
      return renderer(rep);
    } catch {
      return <RawFallback rawValue={getRepString(rep)} />;
    }
  };

  if (populatedReps.length === 0) {
    return <EmptyDash />;
  }
  if (populatedReps.length === 1) {
    return renderOne(populatedReps[0]);
  }
  return (
    <span className={tw`flex flex-col gap-1`}>
      {populatedReps.map((rep, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: repetitions are positional and stable
        <span key={index}>{renderOne(rep)}</span>
      ))}
    </span>
  );
};

/**
 * Inline display text for a CWE/CE/CNE field, if any. Used by the deprecated-description
 * deduplication in the row builder — when DG1-4's text exactly matches the inline display of
 * DG1-3's CWE, we hide DG1-4 to avoid the redundant row.
 */
export const getCodedInlineDisplay = (field: HL7Field): string => {
  try {
    return field.repetition(0).component(CODED_TEXT).toHL7String()?.trim() ?? '';
  } catch {
    return '';
  }
};
